import { filterRoutes, SearchableRoute, useSearchableRoutes } from 'components/command-palette/useSearchableRoutes';
import { useGlobalSearch } from 'hooks/useGlobalSearch';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GLOBAL_SEARCH_SECTION_ORDER } from './entityDisplay';
import {
    GlobalSearchEntityType,
    GlobalSearchResponse,
    GlobalSearchRow as GlobalSearchRowDto,
    GlobalSearchSection as GlobalSearchSectionDto,
} from 'types/globalSearch';
import { pathForResult, searchResultsPath } from 'utils/globalSearchRoutes';
import { clearRecentlyViewed, readRecentlyViewed, RecentlyViewedRecord } from 'utils/recentlyViewed';
import { captureSearchResultClicked, captureSearchResultsPageOpened, SearchSurface } from 'utils/searchAnalytics';

const PAGE_LIMIT = 5;

export interface GlobalSearchOption {
    id: string;
    select: () => void;
}

type DrawnRow<T> = T & { id: string; select: () => void };

export interface GlobalSearchOptionsModel {
    trimmed: string;
    data: GlobalSearchResponse | undefined;
    isLoading: boolean;
    isError: boolean;
    /** The search matched too much to rank in time, as `useGlobalSearch` reports it. */
    isTimedOut: boolean;
    showRecents: boolean;
    recentRows: DrawnRow<{ record: RecentlyViewedRecord }>[];
    bestBet: DrawnRow<{ row: GlobalSearchRowDto }> | null;
    pageRows: DrawnRow<{ route: SearchableRoute }>[];
    sectionRows: { section: GlobalSearchSectionDto; rows: DrawnRow<{ row: GlobalSearchRowDto }>[] }[];
    // Flat and in drawing order, so the arrows cross section boundaries and the position an
    // analytics event reports is the position the user saw.
    options: GlobalSearchOption[];
    /** Where the selection starts. Never on a recent: nobody asked to open one. */
    firstId: string | undefined;
    clearRecents: () => void;
    openResultsPage: (entityType?: GlobalSearchEntityType) => void;
}

export interface UseGlobalSearchOptionsArgs {
    query: string;
    listboxId: string;
    surface: SearchSurface;
    /** Nothing is asked of the backend while the panel is away. */
    open: boolean;
    close: () => void;
    /** List only this family's records, and no pages. Unset lists everything. */
    entityType?: GlobalSearchEntityType;
}

/**
 * Everything the typeahead panel lists, and what opening each line does.
 *
 * It lives beside the search field, not inside the panel, because the field is what names the
 * selected option in `aria-activedescendant`: known anywhere else, the selection the panel starts
 * on could only be pushed up out of an effect, a render behind what is drawn.
 */
export function useGlobalSearchOptions({
    query,
    listboxId,
    surface,
    open,
    close,
    entityType,
}: UseGlobalSearchOptionsArgs): GlobalSearchOptionsModel {
    const navigate = useNavigate();
    const { data, isLoading, isError, isTimedOut, isTooShort } = useGlobalSearch({
        query,
        mode: 'DROPDOWN',
        surface,
        skip: !open,
    });
    const routes = useSearchableRoutes();
    const trimmed = query.trim();

    // Re-read per opening, not per render: it comes out of `localStorage`, and what was opened
    // since last time belongs in this list.
    const [recents, setRecents] = useState(readRecentlyViewed);
    const [wasOpen, setWasOpen] = useState(open);
    if (wasOpen !== open) {
        setWasOpen(open);
        if (open) setRecents(readRecentlyViewed());
    }

    // Pages match here, on every keystroke, against routes the client already has: no debounce and
    // no request stands between the box and them, so they are drawn from the first character typed
    // and while the records are still on their way.
    const pages = useMemo(
        () =>
            (entityType || trimmed === '' ? [] : filterRoutes(routes, trimmed))
                // A route with a parameter in it has no id to put there, so the row would go nowhere.
                .filter((route) => !route.isDynamic)
                .slice(0, PAGE_LIMIT),
        [routes, trimmed, entityType]
    );

    const isListed = (type: GlobalSearchEntityType) => !entityType || type === entityType;

    const sections = useMemo(
        () =>
            (data?.sections ?? [])
                .filter((section) => !entityType || section.entityType === entityType)
                .toSorted(
                    (a, b) => GLOBAL_SEARCH_SECTION_ORDER[a.entityType] - GLOBAL_SEARCH_SECTION_ORDER[b.entityType]
                ),
        [data, entityType]
    );

    const openPath = (href: string) => {
        close();
        navigate(href);
    };

    const openResult = (row: GlobalSearchRowDto, position: number, bestBet: boolean) => {
        captureSearchResultClicked({ surface, entityType: row.entityType, position, bestBet });
        openPath(pathForResult(row.entityType, row.id));
    };

    const showRecents = isTooShort;
    // The records are drawn only once their answer is in: the skeleton and the error line stand in
    // for them and draw no rows, so neither may leave a record option registered.
    const showRecords = !showRecents && !isLoading && !isError;
    const options: GlobalSearchOption[] = [];
    const optionId = (suffix: string) => `${listboxId}-${suffix}`;

    const register = <T>(id: string, select: () => void, rest: T): DrawnRow<T> => {
        options.push({ id, select });
        return { ...rest, id, select };
    };

    // Registered in drawing order: the exact hit, the pages, then either what was opened recently or
    // the records' sections — never both, as the query is either too short to ask about or not.
    const bestBetRow = showRecords && data?.bestBet && isListed(data.bestBet.entityType) ? data.bestBet : null;
    const bestBet = bestBetRow
        ? register(optionId('best-bet'), () => openResult(bestBetRow, 0, true), { row: bestBetRow })
        : null;

    const pageRows = pages.map((route) =>
        register(optionId(`page-${route.path}`), () => openPath(route.path), { route })
    );

    const recentRows = (showRecents ? recents.filter((record) => isListed(record.entityType)) : []).map((record) =>
        register(
            optionId(`recent-${record.entityType}-${record.id}`),
            () => openPath(pathForResult(record.entityType, record.id)),
            { record }
        )
    );

    const sectionRows = (showRecords ? sections : []).map((section) => ({
        section,
        rows: section.rows.map((row) => {
            const position = options.length;
            return register(optionId(`${row.entityType}-${row.id}`), () => openResult(row, position, false), { row });
        }),
    }));

    return {
        trimmed,
        data,
        isLoading,
        isError,
        isTimedOut,
        showRecents,
        recentRows,
        bestBet,
        pageRows,
        sectionRows,
        options,
        // A page matched what was typed, so it is an answer even below the minimum length; what was
        // opened recently is not, and is never marked on arrival.
        firstId: showRecents ? pageRows[0]?.id : options[0]?.id,
        clearRecents: () => {
            clearRecentlyViewed();
            setRecents([]);
        },
        openResultsPage: (entityType?: GlobalSearchEntityType) => {
            captureSearchResultsPageOpened(surface);
            openPath(searchResultsPath(trimmed, entityType));
        },
    };
}
